import React, { useEffect, useState, useCallback } from "react";
import { Line, Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { getPatientStats, getGames } from "../../services/GameService";
import { formatNum } from "../Patient/StatsDisplay";
import "./PatientStatistics.css";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend
);

// Admin Mode: compare statistics across a clinician's patients, with per-patient
// score trends and game/date filters. Data from /api/admin/patient-stats/.
const PatientStatistics = () => {
  const [patients, setPatients] = useState([]);
  const [games, setGames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [filters, setFilters] = useState({
    game: "",
    date_from: "",
    date_to: "",
  });

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const params = {};
      if (filters.game) params.game = filters.game;
      if (filters.date_from) params.date_from = filters.date_from;
      if (filters.date_to) params.date_to = filters.date_to;
      const data = await getPatientStats(params);
      setPatients(data.patients || []);
    } catch (e) {
      setError("Could not load statistics.");
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    getGames()
      .then(setGames)
      .catch(() => {});
  }, []);

  const handleFilter = (e) =>
    setFilters((f) => ({ ...f, [e.target.name]: e.target.value }));

  const comparisonData = {
    labels: patients.map((p) => p.username),
    datasets: [
      {
        label: "Average score",
        data: patients.map((p) => p.avg_score),
        backgroundColor: "#4a7c59",
      },
    ],
  };

  return (
    <div className="patient-statistics">
      <h1>Patient statistics</h1>

      <div className="ps-filters">
        <label>
          Game
          <select name="game" value={filters.game} onChange={handleFilter}>
            <option value="">All games</option>
            {games.map((g) => (
              <option key={g.game_id} value={g.game_id}>
                {g.game_name}
              </option>
            ))}
          </select>
        </label>
        <label>
          From
          <input
            type="date"
            name="date_from"
            value={filters.date_from}
            onChange={handleFilter}
          />
        </label>
        <label>
          To
          <input
            type="date"
            name="date_to"
            value={filters.date_to}
            onChange={handleFilter}
          />
        </label>
      </div>

      {error && (
        <p className="ps-error" role="alert">
          {error}
        </p>
      )}

      {loading ? (
        <p>Loading…</p>
      ) : patients.length === 0 ? (
        <p className="ps-empty">No patient data for the selected filters.</p>
      ) : (
        <>
          <table className="ps-table">
            <thead>
              <tr>
                <th>Patient</th>
                <th>Sessions</th>
                <th>Completed</th>
                <th>Avg score</th>
              </tr>
            </thead>
            <tbody>
              {patients.map((p) => (
                <tr key={p.patient_id}>
                  <td>{p.username}</td>
                  <td>{p.sessions_played}</td>
                  <td>{p.completed}</td>
                  <td>{formatNum(p.avg_score)}</td>
                </tr>
              ))}
            </tbody>
          </table>

          <div className="ps-chart">
            <h2>Average score comparison</h2>
            <Bar data={comparisonData} />
          </div>

          {patients
            .filter((p) => p.sessions.length > 0)
            .map((p) => (
              <div className="ps-chart" key={p.patient_id}>
                <h3>{p.username} — score over time</h3>
                <Line
                  data={{
                    labels: p.sessions.map((s) =>
                      new Date(s.started_at).toLocaleDateString()
                    ),
                    datasets: [
                      {
                        label: "Score",
                        data: p.sessions.map((s) => s.score),
                        borderColor: "#4a7c59",
                        backgroundColor: "#4a7c59",
                      },
                    ],
                  }}
                />
              </div>
            ))}
        </>
      )}
    </div>
  );
};

export default PatientStatistics;
